import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtDateTime, type ExportBatchDTO, type ExportMethod, type ExportPreflightDTO, type ExportProblem, type ExportRow } from '@em/shared';
import { api, qk, queryClient, usePeriod, useSettings } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { hrs, openDownload } from '../../components/format';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Hours, Loading, PageHeader, PeriodBadge, QueryState } from '../../components/ui';

const PROBLEM_ORDER: ExportProblem['kind'][] = ['period_not_locked', 'already_exported', 'myob_not_connected', 'employee_unmatched', 'category_unmapped', 'job_unmapped'];
const BATCH_TONE = { pending: 'grey', succeeded: 'green', partial: 'amber', failed: 'red' } as const;

function problemLink(p: ExportProblem, periodId: string) {
  switch (p.kind) {
    case 'employee_unmatched':
      return p.employeeId ? <Link to={`/employees/${p.employeeId}`}>Fix employee</Link> : null;
    case 'category_unmapped':
      return <Link to="/settings/categories">Map categories</Link>;
    case 'myob_not_connected':
      return <Link to="/settings/myob">MYOB connection</Link>;
    case 'job_unmapped':
      return <Link to="/settings/jobs">Jobs</Link>;
    case 'period_not_locked':
      return <Link to={`/periods/${periodId}`}>Go to the period</Link>;
    default:
      return null;
  }
}

export function ExportPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const period = usePeriod(id);
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const [methodChoice, setMethodChoice] = useState<ExportMethod | null>(null);
  const method: ExportMethod = methodChoice ?? settings.data?.exportMethod ?? 'file';
  const [runError, setRunError] = useState<unknown>(null);
  const [running, setRunning] = useState(false);
  const [reexporting, setReexporting] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);

  // ApiClient.exportPreflight has no method argument; the route accepts ?method=, so pass it through the client's typed get.
  const preflight = useQuery({
    queryKey: qk.preflight(id, method),
    queryFn: () => api.get<ExportPreflightDTO>(`/api/pay-periods/${id}/export/preflight`, { method }),
    enabled: !!settings.data,
  });
  const preview = useQuery({ queryKey: qk.preview(id), queryFn: () => api.exportPreview(id) });
  const batches = useQuery({ queryKey: qk.batches(id), queryFn: () => api.exportBatches({ payPeriodId: id }) });
  const shownBatch = batches.data?.find((b) => b.id === (batchId ?? batches.data?.[0]?.id));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['export'] });
    void queryClient.invalidateQueries({ queryKey: ['export-batches'] });
    void queryClient.invalidateQueries({ queryKey: ['pay-periods'] });
    void queryClient.invalidateQueries({ queryKey: ['pay-period'] });
  };

  const run = async (reexport: boolean, reason?: string) => {
    setRunning(true);
    setRunError(null);
    try {
      const b = await api.runExport(id, { method, reexport, reason: reason ?? null });
      setBatchId(b.id);
      refresh();
      toast.push({ kind: b.status === 'succeeded' ? 'success' : b.status === 'failed' ? 'error' : 'warning', title: `Export ${b.status}`, body: 'See the reconciliation report below.' });
    } catch (e) {
      setRunError(e);
      if (reexport) throw e;
    } finally {
      setRunning(false);
    }
  };

  return (
    <QueryState q={period} loadingLabel="Loading pay period">
      {({ period: p }) => (
        <>
          <PageHeader
            breadcrumb={
              <>
                <Link to="/periods">Pay periods</Link> / <Link to={`/periods/${id}`}>{fmtDate(p.startDate)} – {fmtDate(p.endDate)}</Link>
              </>
            }
            title="Payroll export"
            subtitle={
              <span className="row">
                <PeriodBadge status={p.status} /> {fmtDate(p.startDate)} – {fmtDate(p.endDate)}
              </span>
            }
          />

          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
            <Card title="1. Method">
              <div className="stack">
                {(['file', 'api'] as const).map((m) => (
                  <label key={m} className="check" style={{ alignItems: 'flex-start' }}>
                    <input type="radio" name="method" checked={method === m} onChange={() => setMethodChoice(m)} />
                    <span>
                      <strong style={{ marginRight: 6 }}>{m === 'file' ? 'Import file' : 'Send to MYOB (API)'}</strong>
                      {settings.data?.exportMethod === m && <Badge tone="accent">Default</Badge>}
                      <div className="small muted">
                        {m === 'file' ? 'Tab-separated timesheet files, one per week, for File > Import/Export Assistant. Employees match by card ID.' : 'Posts timesheets straight into the connected AccountRight company file.'}
                      </div>
                    </span>
                  </label>
                ))}
              </div>
            </Card>
            <Card title="2. Pre-flight checks" actions={<Button size="sm" variant="ghost" onClick={() => void preflight.refetch()} loading={preflight.isFetching}>Re-check</Button>}>
              <QueryState q={preflight}>
                {(pf) =>
                  pf.problems.length === 0 ? (
                    <Alert tone="green" title="Ready to export">All employees and categories are mapped.</Alert>
                  ) : (
                    <div className="stack tight">
                      <Alert tone={pf.ok ? 'amber' : 'red'} title={pf.ok ? 'Warnings (export can still run)' : `${pf.problems.length} problem${pf.problems.length === 1 ? '' : 's'} to fix first`} />
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {[...pf.problems].sort((a, b) => PROBLEM_ORDER.indexOf(a.kind) - PROBLEM_ORDER.indexOf(b.kind)).map((pr, i) => (
                          <li key={i}>
                            {pr.message} {problemLink(pr, id)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                }
              </QueryState>
            </Card>
          </div>

          <Card title="3. Preview" className="section" tight>
            <QueryState q={preview} loadingLabel="Building preview">
              {(pv) => (
                <>
                  {Object.keys(pv.cashHours).length > 0 && (
                    <div style={{ padding: '12px 16px 0' }}>
                      <Alert tone="accent">
                        Not sent to MYOB (paid in cash): {Object.entries(pv.cashHours).map(([c, h]) => `${c} ${hrs(h)}`).join(', ')}. See Reports, Cash hours.
                      </Alert>
                    </div>
                  )}
                  <Preview rows={pv.rows} weeks={pv.weeks} totalsByEmployee={pv.totalsByEmployee} totalsByCategory={pv.totalsByCategory} />
                </>
              )}
            </QueryState>
          </Card>

          <Card title="4. Run" className="section">
            <div className="stack">
              <ErrorAlert error={runError} title={runError ? 'The export did not run' : undefined} />
              {p.status === 'exported' ? (
                <div className="row">
                  <span>This period has been exported.</span>
                  {can('owner') ? (
                    <Button variant="danger" onClick={() => setReexporting(true)}>
                      Re-export with a reason
                    </Button>
                  ) : (
                    <span className="muted">Only the owner can re-export.</span>
                  )}
                </div>
              ) : (
                <div className="row">
                  <Button variant="primary" size="lg" loading={running} disabled={!preflight.data?.ok || p.status !== 'locked'} onClick={() => void run(false)}>
                    Run export ({method === 'file' ? 'import file' : 'MYOB API'})
                  </Button>
                  {p.status === 'open' && <span className="muted">Lock the period first.</span>}
                </div>
              )}
            </div>
          </Card>

          <Card title="Export batches" className="section" tight>
            <QueryState q={batches}>
              {(list) =>
                list.length === 0 ? (
                  <EmptyState title="Not exported yet" />
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>By</th>
                        <th>Reason</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((b) => (
                        <tr key={b.id} style={b.id === shownBatch?.id ? { background: 'var(--accent-soft)' } : undefined}>
                          <td className="nowrap">{fmtDateTime(b.createdAt, tz)}</td>
                          <td>{b.method === 'file' ? 'Import file' : 'API'}</td>
                          <td>
                            <Badge tone={BATCH_TONE[b.status]}>{b.status}</Badge>
                          </td>
                          <td>{b.createdByName}</td>
                          <td>{b.reason ?? <span className="muted">–</span>}</td>
                          <td className="actions">
                            <Button size="sm" onClick={() => setBatchId(b.id)} disabled={b.id === shownBatch?.id}>
                              {b.id === shownBatch?.id ? 'Showing' : 'Show'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </QueryState>
          </Card>

          {shownBatch && <BatchReport batch={shownBatch} tz={tz} />}

          {reexporting && (
            <PromptDialog
              title="Re-export this period"
              description="Re-exporting sends the period again. MYOB may then hold duplicate timesheets, so remove the earlier import first. The API refuses if nothing has changed."
              label="Reason for re-exporting"
              required
              danger
              confirmLabel="Re-export"
              onClose={() => setReexporting(false)}
              onConfirm={(reason) => run(true, reason)}
            />
          )}
        </>
      )}
    </QueryState>
  );
}

function Preview({
  rows,
  weeks,
  totalsByEmployee,
  totalsByCategory,
}: {
  rows: ExportRow[];
  weeks: { weekStart: string; weekEnd: string }[];
  totalsByEmployee: { employeeId: string; employeeName: string; hours: number; previousHours: number | null }[];
  totalsByCategory: Record<string, number>;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, { name: string; number: string; weeks: Map<string, ExportRow[]> }>();
    for (const r of rows) {
      const g = m.get(r.employeeId) ?? { name: r.employeeName, number: r.employeeNumber, weeks: new Map() };
      g.weeks.set(r.weekStart, [...(g.weeks.get(r.weekStart) ?? []), r]);
      m.set(r.employeeId, g);
    }
    return [...m.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [rows]);
  const totals = new Map(totalsByEmployee.map((t) => [t.employeeId, t]));
  const negatives = rows.filter((r) => r.hours < 0).length;

  if (rows.length === 0) return <EmptyState title="Nothing to export">No approved hours in this period.</EmptyState>;
  return (
    <>
      {negatives > 0 && (
        <div className="card-body">
          <Alert tone="amber" title={`${negatives} negative adjustment${negatives === 1 ? '' : 's'}`}>
            MYOB timesheets cannot take negative hours. These rows are highlighted as manual deductions: enter them in the pay run by hand.
          </Alert>
        </div>
      )}
      <div className="table-wrap">
        <table className="table compact">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>MYOB category</th>
              <th>Job</th>
              <th className="num">Hours</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([empId, g]) => {
              const t = totals.get(empId);
              const delta = t && t.previousHours != null ? Math.round((t.hours - t.previousHours) * 100) / 100 : null;
              return (
                <EmployeeRows key={empId} name={g.name} number={g.number} weeks={weeks} groupWeeks={g.weeks} total={t?.hours ?? null} previous={t?.previousHours ?? null} delta={delta} />
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="card-body">
        <h3 style={{ marginBottom: 6 }}>Totals by category</h3>
        <div className="row">
          {Object.entries(totalsByCategory).map(([c, h]) => (
            <Badge key={c}>
              {c}: {hrs(h)}
            </Badge>
          ))}
        </div>
      </div>
    </>
  );
}

function EmployeeRows({
  name,
  number,
  weeks,
  groupWeeks,
  total,
  previous,
  delta,
}: {
  name: string;
  number: string;
  weeks: { weekStart: string; weekEnd: string }[];
  groupWeeks: Map<string, ExportRow[]>;
  total: number | null;
  previous: number | null;
  delta: number | null;
}) {
  return (
    <>
      <tr className="group">
        <td colSpan={4}>
          {name} <span className="muted small">{number}</span>
          {previous != null && (
            <span className="small muted" style={{ fontWeight: 400 }}>
              {' '}
              · previous period {hrs(previous)} h{delta ? <span className={delta > 0 ? 'diff-up' : 'diff-down'}> ({delta > 0 ? '+' : ''}{hrs(delta)})</span> : ''}
            </span>
          )}
        </td>
        <td className="num">
          <Hours value={total} />
        </td>
        <td />
      </tr>
      {weeks
        .filter((w) => groupWeeks.has(w.weekStart))
        .map((w) => (
          <WeekRows key={w.weekStart} week={w} rows={groupWeeks.get(w.weekStart)!} showWeek={weeks.length > 1} />
        ))}
    </>
  );
}

function WeekRows({ week, rows, showWeek }: { week: { weekStart: string; weekEnd: string }; rows: ExportRow[]; showWeek: boolean }) {
  return (
    <>
      {showWeek && (
        <tr>
          <td colSpan={6} className="small muted">
            Week {fmtDate(week.weekStart)} – {fmtDate(week.weekEnd)}
          </td>
        </tr>
      )}
      {rows.map((r, i) => (
        <tr key={i} className={r.hours < 0 ? 'highlight' : ''}>
          <td className="nowrap">{fmtDate(r.date)}</td>
          <td>{r.category}</td>
          <td>{r.myobCategoryName ?? <span className="red">Not mapped</span>}</td>
          <td>{r.jobCode ?? <span className="muted">–</span>}</td>
          <td className={`num ${r.hours < 0 ? 'red strong' : ''}`}>
            <Hours value={r.hours} />
          </td>
          <td className="small">
            {r.hours < 0 && <Badge tone="red">Manual deduction</Badge>} {r.note}
          </td>
        </tr>
      ))}
    </>
  );
}

function BatchReport({ batch, tz }: { batch: ExportBatchDTO; tz: string }) {
  const toast = useToast();
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const r = batch.report;
  return (
    <Card
      className="section"
      title={
        <span className="row">
          <h2>Reconciliation</h2>
          <Badge tone={BATCH_TONE[batch.status]}>{batch.status}</Badge>
          <span className="small muted">{fmtDateTime(batch.createdAt, tz)}</span>
        </span>
      }
      actions={
        <div className="row">
          {batch.files.map((f) => (
            <Button key={f} size="sm" variant={f.endsWith('.pdf') ? 'primary' : 'default'} onClick={() => void openDownload(`/api/export-batches/${batch.id}/files/${encodeURIComponent(f)}`, toast.error)}>
              {f.endsWith('.pdf') ? 'Download PDF report' : `Download ${f}`}
            </Button>
          ))}
        </div>
      }
    >
      {!r ? (
        batch.status === 'pending' ? (
          <Loading label="Export running" />
        ) : (
          <EmptyState title="No report recorded for this batch" />
        )
      ) : (
        <div className="stack">
          <div className="grid grid-4">
            <div className="stat card">
              <div className="label">Lines sent</div>
              <div className="value">{r.linesSent}</div>
            </div>
            <div className="stat card">
              <div className="label">Accepted</div>
              <div className="value green">{r.linesAccepted}</div>
            </div>
            <div className="stat card">
              <div className="label">Rejected</div>
              <div className={`value ${r.linesRejected ? 'red' : ''}`}>{r.linesRejected}</div>
            </div>
            <div className="stat card">
              <div className="label">Method</div>
              <div className="value" style={{ fontSize: 16 }}>
                {r.method === 'file' ? 'Import file' : 'MYOB API'}
              </div>
            </div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(220px, 1fr)' }}>
            <table className="table compact card">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="num">Lines</th>
                  <th className="num">Accepted</th>
                  <th className="num">Rejected</th>
                  <th className="num">Hours</th>
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {r.byEmployee.map((e) => (
                  <tr key={e.employeeId}>
                    <td className="strong">{e.employeeName}</td>
                    <td className="num">{e.lines}</td>
                    <td className="num">{e.accepted}</td>
                    <td className={`num ${e.rejected ? 'red strong' : ''}`}>{e.rejected}</td>
                    <td className="num">
                      <Hours value={e.hours} />
                    </td>
                    <td className="small red">{e.errors.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="table compact card">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(r.byCategory).map(([c, h]) => (
                  <tr key={c}>
                    <td>{c}</td>
                    <td className="num">{hrs(h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.checklist.length > 0 && (
            <div>
              <h3 style={{ marginBottom: 4 }}>Checklist</h3>
              <p className="small muted">For your own tracking; ticks are not saved.</p>
              <ul className="checklist">
                {r.checklist.map((c, i) => (
                  <li key={i}>
                    <label className="check" style={{ alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={ticked.has(i)}
                        onChange={(e) =>
                          setTicked((s) => {
                            const n = new Set(s);
                            if (e.target.checked) n.add(i);
                            else n.delete(i);
                            return n;
                          })
                        }
                      />
                      <span style={ticked.has(i) ? { textDecoration: 'line-through', color: 'var(--text-3)' } : undefined}>{c}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
